import { BUILDINGS, UNITS } from "../content/buildings.js";
import { queueEtaProjection } from "../economy-queue-projection.js";
import {
  floorRational,
  formatMicro,
  type Micro,
  microFromString,
  rational,
  rationalFromDecimal,
} from "../exact-numbers.js";
import type { ManufacturingOrder, QueueKind } from "../manufacturing-types.js";
import { effectsForResearch } from "../research-effects.js";
import type { SeatObservation } from "../types.js";
import type { QueueDraft } from "./app-source.js";
import {
  appAction,
  draftIntent,
  gameAction,
  move,
  type AppNode as Node,
  name,
  text,
} from "./view-nodes.js";

export function queueComposer(
  kind: QueueKind,
  observation: SeatObservation,
  draft: QueueDraft,
): Node {
  const targets = queueTargets(kind, observation);
  const selected = targets.some((target) => target.id === draft.target)
    ? draft.target
    : (targets[0]?.id ?? draft.target);
  if (targets.length === 0) return text(`${kind}-none`, "No restorable wreckage is available.");
  return {
    kind: "stack",
    id: `${kind}-composer`,
    children: [
      {
        kind: "select-input",
        id: `${kind}-target`,
        label: "Target",
        value: selected,
        options: targets.map((target) => ({ value: target.id, label: target.name })),
        intent: (target) => draftIntent(kind, { target }),
      },
      {
        kind: "text-input",
        id: `${kind}-quantity`,
        label: "Quantity",
        value: draft.quantity,
        intent: (quantity) => draftIntent(kind, { quantity }),
      },
      {
        kind: "row",
        id: `${kind}-shortcuts`,
        children: ["1", "5", "10"].map((quantity) =>
          appAction(`${kind}-${quantity}`, quantity, draftIntent(kind, { quantity })),
        ),
      },
      {
        kind: "toggle-input",
        id: `${kind}-repeat`,
        label: "Repeat",
        value: draft.repeat,
        intent: (repeat) => draftIntent(kind, { repeat }),
      },
      gameAction(`${kind}-enqueue`, enqueueLabel(kind, selected, draft.quantity, observation), {
        kind: "enqueue-queue-order",
        queue: kind,
        target: selected as never,
        quantity: draft.quantity,
        repeat: draft.repeat,
      }),
    ],
  };
}

export function queueView(kind: QueueKind, observation: SeatObservation): Node {
  const state = observation.self.manufacturing;
  const entries = state[kind];
  const eta = queueEtaProjection(observation, kind);
  return {
    kind: "infobox",
    id: `${kind}-queue`,
    title: `${name(kind)} queue — ${entries.length}`,
    initiallyOpen: true,
    content: [
      ...(eta
        ? [
            text(
              `${kind}-eta`,
              eta.etaSeconds === null
                ? `No progress at current allocations. ${eta.explanation}`
                : `Conditional ETA ${eta.etaSeconds}s at ${eta.ratePerSecond} capacity/s. ${eta.explanation}`,
            ),
          ]
        : []),
      gameAction(`${kind}-pause`, state.paused[kind] ? "Resume queue" : "Pause queue", {
        kind: "set-queue-paused",
        queue: kind,
        paused: !state.paused[kind],
      }),
      ...entries.flatMap((entry, index) => orderRow(kind, entry, index, entries)),
    ],
  };
}

function orderRow(
  kind: QueueKind,
  entry: ManufacturingOrder,
  index: number,
  entries: readonly ManufacturingOrder[],
): readonly Node[] {
  return [
    text(
      `${entry.id}-summary`,
      `${index + 1}. ${name(entry.target)} — ${entry.remaining} of ${entry.quantity} remaining${entry.repeat ? "; Repeat" : ""}`,
    ),
    {
      kind: "row",
      id: `${entry.id}-controls`,
      children: [
        gameAction(`${entry.id}-repeat`, entry.repeat ? "Stop Repeat" : "Repeat", {
          kind: "set-queue-repeat",
          queue: kind,
          orderId: entry.id,
          repeat: !entry.repeat,
        }),
        gameAction(`${entry.id}-cancel`, "Cancel", {
          kind: "cancel-queue-order",
          queue: kind,
          orderId: entry.id,
        }),
        ...(index > 0
          ? [
              gameAction(`${entry.id}-up`, "Move up", {
                kind: "reorder-queue",
                queue: kind,
                orderIds: move(
                  entries.map((item) => item.id),
                  index,
                  index - 1,
                ),
              }),
            ]
          : []),
      ],
    },
  ];
}

function queueTargets(kind: QueueKind, observation: SeatObservation) {
  if (kind === "assembly") return UNITS;
  if (kind === "restoration")
    return BUILDINGS.filter((rule) => Number(observation.self.wreckage[rule.id]) > 0);
  const unlocks = effectsForResearch(observation.self.research).unlocks;
  return BUILDINGS.filter((rule) => unlocks.has(rule.id));
}

function enqueueLabel(
  kind: QueueKind,
  target: string,
  quantity: string,
  observation: SeatObservation,
): string {
  const rule = [...BUILDINGS, ...UNITS].find((item) => item.id === target);
  if (!rule) return `Add to ${name(kind)} queue`;
  try {
    const amount = microFromString(quantity);
    if (amount <= 0n) throw new TypeError("Quantity must be positive");
    let total = (amount * BigInt(rule.metalCost)) as Micro;
    let work = (amount * BigInt(rule.workCost)) as Micro;
    if (kind === "restoration") {
      work = scaleMicro(work, "0.5");
      total = scaleMicro(
        total,
        effectsForResearch(observation.self.research).ratios["restoration-metal"] ?? "0.5",
      );
    }
    return `Add ${quantity} ${rule.name} — ${formatMicro(total)} Metal / ${formatMicro(work)} Work total`;
  } catch {
    return `Add ${rule.name} — enter a positive quantity`;
  }
}

function scaleMicro(value: Micro, ratio: string): Micro {
  const factor = rationalFromDecimal(ratio);
  return floorRational(rational(value * factor.numerator, factor.denominator)) as Micro;
}
